import { Scene, Vector3 } from 'three';

import { createCameraObjects, disposeCameraObjects } from './create-camera-objects';
import {
	CAMERA_HELPER_DEFAULTS,
	CameraHelperConfig,
	CameraHelperObjects,
	RenderQuality,
	VisualizableCamera
} from './camera-helper.types';
import { setCameraObjectsVisibility, updateCameraObjects } from './update-camera-objects';

/**
 * Manages visualization for a single camera
 *
 * Creates and maintains cone and target line objects that represent
 * the camera's position and viewing direction.
 *
 * @example
 * ```typescript
 * const helper = new CameraHelper(camera, { scene, visible: true, quality: 5 });
 * helper.setTargetPosition(new Vector3(0, 0, 0));
 *
 * // In render loop
 * helper.update();
 *
 * // Change quality
 * helper.quality = 10;
 *
 * // Clean up
 * helper.dispose();
 * ```
 */
export class CameraHelper {
	/** The camera being visualized */
	readonly camera: VisualizableCamera;

	/** Scene reference */
	#scene: Scene;

	/** Visualization objects */
	#objects: CameraHelperObjects;

	/** Configuration */
	#coneRadius: number;
	#coneHeight: number;
	#coneColor: number;
	#lineColor: number;
	#visible: boolean;
	#targetPosition: Vector3 | null;
	#quality: RenderQuality;

	constructor (camera: VisualizableCamera, config: CameraHelperConfig) {
		this.camera = camera;
		this.#scene = config.scene;
		this.#coneRadius = config.coneRadius ?? CAMERA_HELPER_DEFAULTS.CONE_RADIUS;
		this.#coneHeight = config.coneHeight ?? CAMERA_HELPER_DEFAULTS.CONE_HEIGHT;
		this.#coneColor = config.coneColor ?? CAMERA_HELPER_DEFAULTS.CONE_COLOR;
		this.#lineColor = config.lineColor ?? CAMERA_HELPER_DEFAULTS.LINE_COLOR;
		this.#visible = config.visible ?? false;
		this.#targetPosition = config.targetPosition?.clone() ?? null;
		this.#quality = config.quality ?? CAMERA_HELPER_DEFAULTS.QUALITY;

		// Create visualization objects
		this.#objects = createCameraObjects(
			camera,
			this.#scene,
			this.#coneRadius,
			this.#coneHeight,
			this.#coneColor,
			this.#lineColor,
			this.#visible,
			this.#targetPosition ?? undefined,
			this.#quality
		);
	}

	/** Get visibility state */
	get visible (): boolean {
		return this.#visible;
	}

	/** Set visibility state */
	set visible (value: boolean) {
		this.#visible = value;
		setCameraObjectsVisibility(this.#objects, value);
	}

	/** Get the target position */
	get targetPosition (): Vector3 | null {
		return this.#targetPosition?.clone() ?? null;
	}

	/** Get the cone mesh */
	get cone () {
		return this.#objects.cone;
	}

	/** Get the target line */
	get targetLine () {
		return this.#objects.targetLine;
	}

	/** Get render quality */
	get quality (): RenderQuality {
		return this.#quality;
	}

	/** Set render quality (0=wireframe, 1-10=quality level) */
	set quality (value: RenderQuality) {
		if (this.#quality === value) return;
		this.#quality = value;
		this.recreateObjects();
	}

	/**
	 * Set the target position for the camera visualization
	 */
	setTargetPosition (position: Vector3): void {
		this.#targetPosition = position.clone();
	}

	/**
	 * Update visualization to match current camera state
	 *
	 * @param targetPosition Optional target position override
	 */
	update (targetPosition?: Vector3): void {
		const target = targetPosition ?? this.#targetPosition ?? undefined;
		updateCameraObjects(
			this.camera,
			this.#objects,
			this.#visible,
			target
		);
	}

	/**
	 * Set cone dimensions
	 */
	setConeDimensions (radius: number, height: number): void {
		this.#coneRadius = radius;
		this.#coneHeight = height;
		this.recreateObjects();
	}

	/**
	 * Recreate visualization objects (used when quality or dimensions change)
	 */
	private recreateObjects (): void {
		disposeCameraObjects(this.#objects, this.#scene);
		this.#objects = createCameraObjects(
			this.camera,
			this.#scene,
			this.#coneRadius,
			this.#coneHeight,
			this.#coneColor,
			this.#lineColor,
			this.#visible,
			this.#targetPosition ?? undefined,
			this.#quality
		);
	}

	/**
	 * Dispose of all visualization objects
	 */
	dispose (): void {
		disposeCameraObjects(this.#objects, this.#scene);
	}
}

